import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'

import { PreSaleLotteryApplicantFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'

type LotteryApplicationCardProps = {
  lotteryApplication: PreSaleLotteryApplicantFragment
}

export const LotteryApplicationCard: Organism<LotteryApplicationCardProps> = ({
  lotteryApplication,
}) => {
  const { generatePath, routerPages } = useRouter()
  const { t } = useTranslation('lotteryApplications')

  const { bgcolor, color } = useMemo(() => {
    switch (lotteryApplication.status) {
      case '10_win':
      case '11_win_paid':
        return { bgcolor: 'primary.main', color: 'white' }
      case '20_waiting_payment':
        return { bgcolor: 'error.main', color: 'white' }
      case '30_waiting_lottery':
        return { bgcolor: 'secondary.main', color: 'white' }
      case '40_lose':
        return { bgcolor: 'grey.300', color: 'text.secondary' }
      case '50_invalid':
      case '60_canceled':
      default:
        return { bgcolor: 'grey.100', color: 'text.disabled' }
    }
  }, [lotteryApplication.status])

  /**
   * 表示するSKU
   * 当選以外の場合は第一希望のSKUを表示し
   * 当選している場合は当選したSKUを表示する
   */
  const sku = useMemo(() => {
    if (!lotteryApplication.winChoiceNumber) {
      return lotteryApplication.choiceItemDetails[0].skuUnit
    }
    return lotteryApplication.choiceItemDetails[
      lotteryApplication.winChoiceNumber - 1
    ].skuUnit
  }, [lotteryApplication.winChoiceNumber, lotteryApplication.choiceItemDetails])

  return (
    <OrganismFrame>
      <Card sx={{ borderRadius: 2 }}>
        <CardActionArea
          href={generatePath(routerPages.LotteryApplicationDetail, {
            preSaleId: lotteryApplication.preSale.id,
          })}
        >
          <Stack spacing={1} sx={{ p: 3 }}>
            <Chip
              size="small"
              sx={{
                height: 20,
                borderRadius: '10px',
                bgcolor,
                py: 1,
                px: 4,
                '.MuiChip-label': {
                  px: 0,
                },
              }}
              label={
                <Typography color={color} variant="subtitle2">
                  {t(lotteryApplication.status)}
                </Typography>
              }
            />

            <Typography variant="subtitle1">
              {lotteryApplication.preSale.title}
            </Typography>
            {(!!sku || !!lotteryApplication.preSale.event.title) && (
              <Typography variant="body2" sx={styleSets.lineCamp(2)}>
                {[
                  lotteryApplication?.preSale?.event?.title ?? '',
                  sku?.unitName ?? '',
                ]
                  .filter(o => !!o)
                  .join('/')}
              </Typography>
            )}
          </Stack>
          {!!(
            lotteryApplication.resultNoticedAt ||
            lotteryApplication.preSale.lottery?.resultNoticedAt
          ) && (
            <Box sx={{ bgcolor: 'grey.100', px: 5, py: 2 }}>
              <Typography variant="caption" sx={{ fontSize: '10px' }}>
                {t('resultNoticedAt')}
                {DateTime.formatDate(
                  lotteryApplication.resultNoticedAt ||
                    lotteryApplication.preSale.lottery?.resultNoticedAt,
                  'YYYY/MM/DD(ddd) HH:mm'
                )}
              </Typography>
            </Box>
          )}
        </CardActionArea>
      </Card>
    </OrganismFrame>
  )
}
